import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { getFirebaseApp } from './firebase';
import { api } from './api';

const DEVICE_ID_KEY = 'vaa_web_device_id';

/**
 * Stable id for this browser profile (used as device_id for DeviceRegistration).
 */
export function getOrCreateWebDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Registers the FCM web token with the API using device_type "web".
 * Requires REACT_APP_FCM_VAPID_KEY (Firebase Console → Web Push certificates).
 */
export async function registerWebPushDevice() {
  const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;
  if (!vapidKey) {
    console.warn('[WebPush] REACT_APP_FCM_VAPID_KEY is not set; skipping FCM registration');
    return null;
  }

  if (!(await isSupported())) {
    console.warn('[WebPush] Firebase messaging is not supported in this browser');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('[WebPush] Service workers are not supported');
    return null;
  }

  const app = getFirebaseApp();
  const messaging = getMessaging(app);

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('[WebPush] Notification permission was not granted');
    return null;
  }

  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    console.warn('[WebPush] No FCM registration token returned');
    return null;
  }

  const deviceId = getOrCreateWebDeviceId();

  await api.put('/api/auth/device/register', {
    device_id: deviceId,
    device_type: 'web',
    fcm_token: token,
    device_model: typeof navigator !== 'undefined' && navigator.userAgent
      ? String(navigator.userAgent).slice(0, 200)
      : 'web',
    app_version: process.env.REACT_APP_VERSION || '1.0.0',
    platform: 'web',
    os_version: typeof navigator !== 'undefined' ? navigator.platform || 'unknown' : 'unknown',
  });

  console.log('[WebPush] Registered web device for push notifications');
  return token;
}

/**
 * Subscribe to foreground (in-tab) messages — optional UI hook.
 */
export function subscribeForegroundMessages(onPayload) {
  const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;
  if (!vapidKey) return () => {};

  const messaging = getMessaging(getFirebaseApp());
  return onMessage(messaging, (payload) => {
    if (typeof onPayload === 'function') onPayload(payload);
  });
}
